import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import ConfettiOverlay from "@web/components/ConfettiOverlay";
import { useConfetti } from "@web/hooks/useConfetti";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui";
import { useAuth, useToast } from "@/contexts";
import { getPublicInvitePreview, joinCompetition } from "@/features/competitions/api";
import { getCurrentUserProfile } from "@/features/dashboard/api";
import { getProviderConnections, hasConnectedProvider } from "@/features/sync/connections";
import type { PublicInvitePreview } from "@/types";

type PageState = "loading" | "preview" | "joining" | "joined" | "member" | "expired" | "not-found";

type MobilePlatform = "ios" | "android" | "other";

function getMobilePlatform(): MobilePlatform {
  if (typeof navigator === "undefined") return "other";
  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return "ios";
  if (/android/.test(ua)) return "android";
  return "other";
}

function attemptAppDeepLink(code: string, onFail: () => void): (() => void) | null {
  const platform = getMobilePlatform();
  if (platform === "other") return null;

  const schemeUrl = `footslapping://join/${code}`;

  let timeout: ReturnType<typeof setTimeout>;
  const cleanup = () => {
    clearTimeout(timeout);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };

  const onVisibilityChange = () => {
    if (document.hidden) cleanup();
  };

  document.addEventListener("visibilitychange", onVisibilityChange);

  timeout = setTimeout(() => {
    cleanup();
    onFail();
  }, 2000);

  window.location.href = schemeUrl;

  return cleanup;
}

export default function InviteLanding() {
  const { code } = useParams({ from: "/invite/$code" }) as { code: string };
  const _search = useSearch({ from: "/invite/$code" }) as { ref?: string };
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();

  const [state, setState] = useState<PageState>("loading");
  const [preview, setPreview] = useState<PublicInvitePreview | null>(null);
  const [showStoreBadges, setShowStoreBadges] = useState(false);
  const { fire: fireConfetti, cleanup: cleanupConfetti } = useConfetti();
  const hasFiredRef = useRef(false);
  const platform = getMobilePlatform();

  useEffect(() => {
    if (!code) return;
    const deepLinkCleanup = attemptAppDeepLink(code, () => setShowStoreBadges(true));
    return () => deepLinkCleanup?.();
  }, [code]);

  useEffect(() => {
    if (!code) return;
    (async () => {
      try {
        const { data, error } = await getPublicInvitePreview(code);
        if (error || !data) {
          setState("not-found");
          return;
        }
        setPreview(data);
        if (user) {
          const profile = await getCurrentUserProfile();
          if (profile.data) {
            try {
              const joinResult = await joinCompetition(code);
              if (joinResult.error) {
                setState("preview");
              } else {
                setState("member");
              }
            } catch {
              setState("preview");
            }
          } else {
            setState("preview");
          }
        } else {
          setState("preview");
        }
      } catch {
        setState("not-found");
      }
    })();
  }, [code, user]);

  useEffect(() => {
    return () => cleanupConfetti();
  }, [cleanupConfetti]);

  useEffect(() => {
    if (state === "joined" && !hasFiredRef.current) {
      hasFiredRef.current = true;
      fireConfetti();
    }
  }, [state, fireConfetti]);

  const handleAccept = useCallback(async () => {
    if (!code || state !== "preview") return;
    setState("joining");
    if (!user) {
      navigate({ to: "/sign-in", search: { redirect: `/invite/${code}` } });
      return;
    }

    const connections = await getProviderConnections();
    if (!hasConnectedProvider(connections)) {
      navigate({
        to: "/health-setup",
        search: { redirect: `/invite/${code}` },
        replace: true,
      });
      return;
    }

    const { error, data } = await joinCompetition(code);
    if (error) {
      toast.showAlert("Join failed", error.message, "error");
      setState("preview");
      return;
    }
    setState("joined");
    toast.showAlert(
      "Joined!",
      `You're in${preview ? ` ${preview.competitionName}` : ""}`,
      "success",
    );
    setTimeout(() => {
      navigate({ to: `/competition/${data}`, replace: true });
    }, 2000);
  }, [code, state, user, navigate, toast, preview]);

  if (state === "loading") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0f172a] to-[#1e293b] flex items-center justify-center p-4">
        <div className="animate-pulse text-center">
          <div className="w-20 h-20 bg-[rgba(255,255,255,0.1)] rounded-full mx-auto mb-4" />
          <div className="h-4 bg-[rgba(255,255,255,0.1)] rounded w-48 mx-auto" />
        </div>
      </div>
    );
  }

  if (state === "not-found" || !preview) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0f172a] to-[#1e293b] flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-6xl mb-4 opacity-60" role="presentation">
            🏃
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Invite Not Found</h1>
          <p className="text-[#9e9eb8] mb-6">
            This invite link is invalid or expired. Ask for a new one!
          </p>
          <Button label="Go Home" onPress={() => navigate({ to: "/" })} />
        </div>
      </div>
    );
  }

  const daysUntilStart = Math.max(
    0,
    Math.ceil((new Date(preview.startsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0f172a] to-[#1e293b] flex items-center justify-center p-4">
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0rem); }
          50% { transform: translateY(-0.625rem); }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(1.25rem); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 0 20px rgba(251, 191, 36, 0.3); }
          50% { box-shadow: 0 0 40px rgba(251, 191, 36, 0.6); }
        }
        .invite-card { animation: fadeSlideUp 0.6s ease-out; }
        .invite-avatar { animation: float 3s ease-in-out infinite; }
        .accept-btn:hover { animation: pulseGlow 1.5s ease-in-out infinite; }
        .badge-icon { animation: float 2s ease-in-out infinite; }
        .badge-icon:nth-child(2) { animation-delay: 0.3s; }
        .badge-icon:nth-child(3) { animation-delay: 0.6s; }
      `}</style>

      <div className="invite-card w-full max-w-md">
        <div className="bg-[rgba(255,255,255,0.05)] rounded-[24px] p-8 border border-[rgba(255,255,255,0.1)] backdrop-blur-sm">
          <div className="text-center mb-6">
            <div className="invite-avatar w-20 h-20 rounded-full bg-[rgba(255,255,255,0.1)] mx-auto mb-4 overflow-hidden border-2 border-[rgba(251,191,36,0.5)]">
              {preview.inviterAvatar ? (
                <img
                  src={preview.inviterAvatar}
                  alt={preview.inviterName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-3xl">
                  {preview.inviterName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <h1 className="text-2xl font-bold text-white mb-1">
              {preview.inviterName} challenged you!
            </h1>
            <p className="text-[#fbbf24] text-lg font-semibold">{preview.competitionName}</p>
          </div>

          <div className="flex justify-center gap-2 mb-6">
            {preview.badgePool.slice(0, 5).map((emoji) => (
              <span key={emoji} className="badge-icon text-xl">
                {emoji}
              </span>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-[rgba(255,255,255,0.05)] rounded-xl p-3 text-center">
              <div className="text-[#9e9eb8] text-xs uppercase tracking-wide mb-1">Duration</div>
              <div className="text-white font-semibold">
                {Math.ceil(
                  (new Date(preview.endsAt).getTime() - new Date(preview.startsAt).getTime()) /
                    (1000 * 60 * 60 * 24),
                )}{" "}
                days
              </div>
            </div>
            <div className="bg-[rgba(255,255,255,0.05)] rounded-xl p-3 text-center">
              <div className="text-[#9e9eb8] text-xs uppercase tracking-wide mb-1">Goal</div>
              <div className="text-white font-semibold">
                {preview.dailyGoalSteps
                  ? `${(preview.dailyGoalSteps / 1000).toFixed(0)}k/day`
                  : preview.targetSteps
                    ? `${(preview.targetSteps / 1000).toFixed(0)}k total`
                    : "Step it up!"}
              </div>
            </div>
            <div className="bg-[rgba(255,255,255,0.05)] rounded-xl p-3 text-center">
              <div className="text-[#9e9eb8] text-xs uppercase tracking-wide mb-1">Starts</div>
              <div className="text-white font-semibold">
                {daysUntilStart > 0 ? `In ${daysUntilStart}d` : "Live now"}
              </div>
            </div>
            <div className="bg-[rgba(255,255,255,0.05)] rounded-xl p-3 text-center">
              <div className="text-[#9e9eb8] text-xs uppercase tracking-wide mb-1">
                Participants
              </div>
              <div className="text-white font-semibold">{preview.memberCount}</div>
            </div>
          </div>

          {state === "joined" || state === "member" ? (
            <Button
              label={state === "joined" ? "🎉 You're in! Redirecting..." : "👟 Go to competition"}
              onPress={() => {
                navigate({ to: `/competition/${preview?.competitionId}` });
              }}
            />
          ) : (
            <div className="accept-btn">
              <Button
                label={
                  state === "joining"
                    ? "Joining..."
                    : user
                      ? "🎯 Accept Challenge"
                      : "🎯 Sign in to Accept"
                }
                onPress={handleAccept}
              />
            </div>
          )}

          {!user && state === "preview" && (
            <p className="text-[#9e9eb8] text-xs text-center mt-3">
              Sign in or create an account to accept this challenge
            </p>
          )}

          {showStoreBadges && (
            <div className="mt-4 pt-4 border-t border-[rgba(255,255,255,0.1)] flex flex-col items-center gap-2">
              <p className="text-[#9e9eb8] text-xs text-center">
                Don't have the app yet? Get it on your device:
              </p>
              <div className="flex flex-row items-center gap-3">
                {platform !== "android" && (
                  <a
                    href="https://apps.apple.com/app/id6781928157"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83"
                      alt="Download on the App Store"
                      className="h-9 w-auto"
                    />
                  </a>
                )}
                {platform !== "ios" && (
                  <a
                    href="https://play.google.com/store/apps/details?id=com.footslapping"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                      alt="Get it on Google Play"
                      className="h-9 w-auto"
                    />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {state === "joined" && <ConfettiOverlay badgePool={preview?.badgePool} />}
    </div>
  );
}
